<template>
  <md-button
    :class="computedClasses"
    :disabled="disabled || loading"
    v-bind="$attrs"
    class="btn"
    @click="handleClick"
  >
    <div
      v-if="loading"
      class="btn-loading-container"
    >
      <div class="btn-loading-spinner">
        <div class="spinner-circle"></div>
      </div>
    </div>
    <span
      v-if="!loading || showContentWhileLoading"
      class="btn-content"
      :class="{ 'content-with-spinner': loading && showContentWhileLoading }"
    >
      <slot />
    </span>
  </md-button>
</template>

<script>
export default {
  name: 'ButtonBase',
  inheritAttrs: false,
  props: {
    /**
     * Button variant type
     * @type {String}
     */
    variant: {
      type: String,
      default: 'primary',
      validator: value =>
        [
          'primary',
          'secondary',
          'tertiary',
          'quarternary',
          'quinternary',
          'senary',
          'septenary',
          'octnary',
          'nonenary',
          'orange',
          'green',
          'primary-orange',
          'primary-red',
          'primary-gray',
          'primary-green',
          'secondary-white',
          'secondary-orange',
          'secondary-pink',
          'secondary-blue',
          'secondary-green',
          'secondary-green-water',
          'link-black',
          'dismiss-purple',
          'transparent',
          'cancel',
          'new',
        ].includes(value),
    },

    /**
     * Button size
     * @type {String}
     */
    size: {
      type: String,
      default: 'medium',
      validator: value =>
        ['small', 'medium', 'large', 'block', 'inline', 'regular'].includes(value),
    },

    /**
     * Whether button is disabled
     * @type {Boolean}
     */
    disabled: {
      type: Boolean,
      default: false,
    },

    /**
     * Whether button is in loading state
     * @type {Boolean}
     */
    loading: {
      type: Boolean,
      default: false,
    },

    /**
     * Whether to show button content while loading
     * @type {Boolean}
     */
    showContentWhileLoading: {
      type: Boolean,
      default: false,
    },

    /**
     * Whether button should be raised (Material Design)
     * @type {Boolean}
     */
    raised: {
      type: Boolean,
      default: false,
    },

    /**
     * Whether button should be round (Material Design)
     * @type {Boolean}
     */
    round: {
      type: Boolean,
      default: false,
    },

    /**
     * Button type attribute
     * @type {String}
     */
    type: {
      type: String,
      default: 'button',
      validator: value => ['button', 'submit', 'reset'].includes(value),
    },
  },

  computed: {
    computedClasses() {
      const classes = []

      // Add variant class
      classes.push(`btn-${this.variant}`)

      // Add size classes
      if (this.size === 'small') {
        classes.push('btn-small')
      } else if (this.size === 'large') {
        classes.push('btn-large')
      } else if (this.size === 'block') {
        classes.push('btn-block')
      } else if (this.size === 'inline') {
        classes.push('btn-inline')
      } else if (this.size === 'regular') {
        classes.push('btn-regular-size')
      }

      // Add state classes
      if (this.loading) {
        classes.push('btn-loading')
      }

      // Add Material Design classes
      if (this.raised) {
        classes.push('md-raised')
      }

      if (this.round) {
        classes.push('md-round')
      }

      return classes
    },
  },

  methods: {
    handleClick(event) {
      if (!this.disabled && !this.loading) {
        this.$emit('click', event)
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.btn {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  &.btn-loading {
    opacity: 1 !important;
  }

  &.md-button {
    .md-button-content {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 100%;
      height: 100%;
    }
  }

  &.btn-nonenary {
    color: #7f1a54 !important;
    background-color: transparent !important;

    &:not([disabled]):not(.md-raised) {
      color: #7f1a54 !important;
    }

    &:hover:not([disabled]):not(.md-raised) {
      color: #ffffff !important;
      background-color: #7f1a54 !important;
    }
  }

  &.btn-quarternary {
    color: #7f1a54 !important;
    background-color: #ffffff !important;

    &:not([disabled]):not(.md-raised) {
      color: #7f1a54 !important;
      background-color: #ffffff !important;
    }

    &:hover:not([disabled]):not(.md-raised) {
      color: #691043 !important;
      background-color: #ffffff !important;
    }
  }

  &.btn-secondary {
    color: #7f1a54 !important;
    background-color: transparent !important;

    &:not([disabled]):not(.md-raised) {
      color: #7f1a54 !important;
    }
  }

  &.btn-octnary {
    color: #44ca9e !important;
    background-color: transparent !important;

    &:not([disabled]):not(.md-raised) {
      color: #44ca9e !important;
    }

    &:hover:not([disabled]):not(.md-raised) {
      color: #ffffff !important;
      background-color: #44ca9e !important;
    }
  }
}
.btn-loading-container {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
  z-index: 1;
}

.btn-loading-spinner {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;

  .spinner-circle {
    width: 16px;
    height: 16px;
    border: 2px solid transparent;
    border-top: 2px solid currentColor;
    border-radius: 50%;
    animation: btn-spin 1s linear infinite;
  }
}

.btn-content {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  position: relative;
  z-index: 0;
  gap: 8px;

  .btn-loading:not(.show-content-while-loading) & {
    opacity: 0;
  }

  &.content-with-spinner {
    margin-left: 24px;
    opacity: 1;
  }

  .md-icon {
    margin: 0;
    display: flex;
    align-items: center;
    justify-content: center;
  }
}

@keyframes btn-spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.btn-small {
  .btn-loading-spinner {
    width: 14px;
    height: 14px;

    .spinner-circle {
      width: 14px;
      height: 14px;
      border-width: 1.5px;
      border-top-width: 1.5px;
    }
  }

  .content-with-spinner {
    margin-left: 20px;
  }

  .btn-content {
    gap: 6px;
  }
}

.btn-large {
  .btn-loading-spinner {
    width: 20px;
    height: 20px;

    .spinner-circle {
      width: 20px;
      height: 20px;
      border-width: 2.5px;
      border-top-width: 2.5px;
    }
  }

  .content-with-spinner {
    margin-left: 28px;
  }

  .btn-content {
    gap: 10px;
  }
}
.btn-primary .spinner-circle,
.btn-primary-orange .spinner-circle,
.btn-primary-red .spinner-circle,
.btn-primary-green .spinner-circle,
.btn-orange .spinner-circle,
.btn-green .spinner-circle,
.btn-quinternary .spinner-circle {
  border-top-color: rgba(255, 255, 255, 0.9);
  border-right-color: rgba(255, 255, 255, 0.3);
}

.btn-secondary .spinner-circle,
.btn-secondary-orange .spinner-circle,
.btn-secondary-pink .spinner-circle,
.btn-secondary-blue .spinner-circle,
.btn-secondary-green .spinner-circle,
.btn-secondary-green-water .spinner-circle,
.btn-octnary .spinner-circle,
.btn-nonenary .spinner-circle {
  border-top-color: currentColor;
  border-right-color: rgba(127, 26, 84, 0.3);
}

.btn-tertiary .spinner-circle,
.btn-quarternary .spinner-circle,
.btn-senary .spinner-circle,
.btn-septenary .spinner-circle {
  border-top-color: rgba(0, 0, 0, 0.7);
  border-right-color: rgba(0, 0, 0, 0.3);
}
</style>
